// src/redux/usersSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../actions/configApi'; // Ajuste o caminho conforme sua estrutura

/**
 * Exemplo de thunk para obter a lista de usuários com paginação e/ou busca.
 * Ajuste os parâmetros conforme sua API (por exemplo, ?page, ?search, etc.).
 */
export const fetchUsers = createAsyncThunk(
    'users/fetchUsers',
    async ({ page, searchTerm, sortDirection }, { rejectWithValue }) => {
        try {
            const response = await api.get(`usersAdmin?page=${page}&search=${searchTerm}&sort=${sortDirection}`);
            return response.data; // Estrutura esperada ex: { data: [...], total: 100 }
        } catch (error) {
            if (error.response && error.response.status === 401) {
                return rejectWithValue("Sua sessão expirou. Faça login novamente.");
            }
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

/**
 * Exemplo de thunk para criar um novo usuário.
 * Ajuste o payload conforme os campos necessários para criação.
 */
export const createUser = createAsyncThunk(
    'users/createUser',
    async (newUserData, { rejectWithValue }) => {
        try {
            const response = await api.post('usersAdmin', newUserData);
            return response.data; // Retorna o usuário criado
        } catch (error) {
            if (error.response) {
                // Sessão expirada
                if (error.response.status === 401) {
                    return rejectWithValue("Sua sessão expirou. Faça login novamente.");
                }

                // Erros de validação (status 422)
                if (error.response.status === 422 && error.response.data.errors) {
                    return rejectWithValue(error.response.data.errors);
                }

                // Outros erros com mensagem
                return rejectWithValue(error.response.data.message || "Erro ao criar usuário.");
            }

            // Erro inesperado
            return rejectWithValue("Erro inesperado ao criar usuário.");
        }
    }
);

/**
 * Exemplo de thunk para atualizar um usuário.
 * Ajuste a rota conforme sua API (por exemplo: `users/:id`).
 */
export const updateUser = createAsyncThunk(
    'users/updateUser',
    async ({ userId, userData }, { rejectWithValue }) => {
        try {
            const response = await api.put(`usersAdmin/${userId}`, userData);
            return response.data; // Estrutura esperada: objeto do usuário atualizado
        } catch (error) {
            if (error.response && error.response.status === 401) {
                return rejectWithValue("Sua sessão expirou. Faça login novamente.");
            }
            return rejectWithValue(error.response?.data);
        }
    }
);

/**
 * Exemplo de thunk para deletar um usuário.
 * Ajuste a rota conforme sua API (por exemplo: `users/:id`).
 */
export const deleteUser = createAsyncThunk(
    'users/deleteUser',
    async (userId, { rejectWithValue }) => {
        try {
            const response = await api.delete(`usersAdmin/${userId}`);
            return response.data; // Retornamos o ID para remover no estado local
        } catch (error) {
            if (error.response && error.response.status === 401) {
                return rejectWithValue("Sua sessão expirou. Faça login novamente.");
            }
            return rejectWithValue(error.response?.data);
        }
    }
);

export const restoreUser = createAsyncThunk(
    'users/restoreUser',
    async (userId, { rejectWithValue }) => {
        try {
            const response = await api.post(`usersAdmin/${userId}/restore`);
            return response.data; // Retorna o usuário restaurado
        } catch (error) {
            if (error.response && error.response.status === 401) {
                return rejectWithValue("Sua sessão expirou. Faça login novamente.");
            }
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

const usersSlice = createSlice({
    name: 'users',
    initialState: {
        items: [],            // Lista de usuários
        totalItems: 0,        // Total de registros no back-end
        currentPage: 1,       // Página atual
        itemsPerPage: 10,     // Ajuste se necessário
        loading: false,
        error: null,
        successMessage: null,
        // Filtro e ordenação
        searchTerm: '',
        sortDirection: 'desc',
        // Para focar no item atualizado
        updatedUserId: null,
    },
    reducers: {
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        },
        setSearchTerm: (state, action) => {
            state.searchTerm = action.payload;
            state.currentPage = 1;
            // state.loading = true; // Inicia o carregamento ao buscar
        },
        setSortDirection: (state, action) => {
            state.sortDirection = action.payload;
            state.currentPage = 1;
        },
        clearUpdatedUserId: (state) => {
            state.updatedUserId = null;
        },
        clearMessages: (state) => {
            state.error = null;
            state.successMessage = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch users
            .addCase(fetchUsers.pending, (state) => {
                // alert('pending')
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUsers.fulfilled, (state, action) => {
                state.loading = false;
                state.items = action.payload.data;      // Ajuste se a estrutura for diferente
                state.totalItems = action.payload.total; // Ajuste se a estrutura for diferente
            })
            .addCase(fetchUsers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || action.error.message;
            })

            // Create user
            .addCase(createUser.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.successMessage = null;
            })
            .addCase(createUser.fulfilled, (state, action) => {
                state.loading = false;
                console.log(action.payload.data, 'action.payload2')
                // console.log(action.payload.user, 'action.payload.user')
                state.successMessage = 'Usuário criado com sucesso!';
                state.totalItems = action.payload.total; // Ajuste se a estrutura for diferente
                // Inclui o novo usuário na lista
                
                state.items= action.payload.data;
            })
            .addCase(createUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || action.error.message;
            })

            // Update user
            .addCase(updateUser.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.successMessage = null;
            })
            .addCase(updateUser.fulfilled, (state, action) => {
                state.loading = false;
                state.successMessage = 'Usuário atualizado com sucesso!';
                const updatedUser = action.payload;
                const index = state.items.findIndex((u) => u.id === updatedUser.id);
                if (index !== -1) {
                    state.items[index] = { ...state.items[index], ...updatedUser };
                }
                state.updatedUserId = updatedUser.id;
            })
            .addCase(updateUser.rejected, (state, action) => {
                console.log(action.payload?.errors, 'action.payload')
                state.loading = false;
                state.error = action.payload.errors;
            })

            // Delete user
            .addCase(deleteUser.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.successMessage = null;
            })
            .addCase(deleteUser.fulfilled, (state, action) => {
                state.loading = false;
                //  console.log(action,'action')
                state.successMessage = action.payload.message;
                const deletedUser = action.payload.user;
                state.items = state.items.map((user) =>
                    user?.id === deletedUser?.id ? deletedUser : user
                );

            })
            .addCase(deleteUser.rejected, (state, action) => {
                console.log(action, 'action')
                state.loading = false;
                state.error = action.payload || action.error.message;
            })
            // Restore user
            .addCase(restoreUser.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.successMessage = null;
            })
            .addCase(restoreUser.fulfilled, (state, action) => {
                state.loading = false;
                state.successMessage = 'Usuário ativado com sucesso!';

                // Atualiza o usuário restaurado na lista
                const restoredUser = action.payload.user;
                console.log(restoredUser, 'restoredUser')
                state.items = state.items.map((user) =>
                    user.id === restoredUser.id ? { ...user, deleted_at: null } : user
                );
            })
            .addCase(restoreUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || action.error.message;
            });
    },
});

export const {
    setCurrentPage,
    setSearchTerm,
    setSortDirection,
    clearUpdatedUserId,
    clearMessages,
} = usersSlice.actions;

export default usersSlice.reducer;
